import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../services/apiService";
import { SiteFooter, SiteHeader } from "./SiteChrome";
import { useCmsBatch } from "../hooks/useCmsBatch";

const ageGroupLabels = { all: "All ages", "1-3": "Ages 1-3", "4-5": "Ages 4-5", "6-8": "Ages 6-8", "9-11": "Ages 9-11", "12-19": "Ages 12-19", adult: "Adults" };
const intervalLabels = { monthly: "mo", quarterly: "qtr", annual: "yr", one_time: "one-time" };

function PlanCalculator({ plan }) {
  const [sessions, setSessions] = useState(plan.sessionsPerMonth || 4);
  if (!plan.dropInPriceCents) return null;

  const dropInTotal = plan.dropInPriceCents * sessions;
  const savings = dropInTotal - plan.priceCents;

  return (
    <div className="plan-calculator">
      <div className="plan-calculator-row">
        <span>Sessions per month</span>
        <input
          type="number"
          min="1"
          max="30"
          value={sessions}
          onChange={(e) => setSessions(Math.max(1, Number(e.target.value) || 1))}
        />
      </div>
      <div className="plan-calculator-row">
        <span>Paying per session</span>
        <span>${(dropInTotal / 100).toFixed(0)}/mo</span>
      </div>
      <p className="plan-calculator-savings">
        {savings > 0 ? <>Save <strong>${(savings / 100).toFixed(0)}/mo</strong> with this membership</> : "Membership priced for unlimited access"}
      </p>
    </div>
  );
}

function PlanTile({ plan }) {
  return (
    <article className="program-tile">
      <span className="program-tile-age">{ageGroupLabels[plan.ageGroup] || plan.ageGroup}</span>
      <h3>{plan.name}</h3>
      <p>{plan.description || "Details coming soon."}</p>
      <div className="program-tile-meta">
        <span><strong>${(plan.priceCents / 100).toFixed(0)}</strong>/{intervalLabels[plan.billingInterval] || plan.billingInterval}</span>
        <span><strong>{plan.sessionsPerMonth}</strong> sessions/mo</span>
      </div>
      {plan.features?.length > 0 && (
        <ul style={{ margin: "6px 0 0", paddingLeft: 18, color: "var(--muted)", fontSize: 13, lineHeight: 1.9 }}>
          {plan.features.map((feature) => <li key={feature}>{feature}</li>)}
        </ul>
      )}
      <PlanCalculator plan={plan} />
      <Link className="button button--lime" to={`/checkout/${plan.slug}`} style={{ marginTop: 22 }}>Get started</Link>
    </article>
  );
}

export function MembershipsPage() {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");
  const [ageGroup, setAgeGroup] = useState("all");
  const { content } = useCmsBatch(["memberships-hero"]);
  const hero = content["memberships-hero"] || {};

  useEffect(() => {
    api.get("/memberships/plans")
      .then((response) => setPlans(response.data.plans || []))
      .catch((e) => setError(e.message));
  }, []);

  const ageGroups = useMemo(() => [...new Set(plans.map((p) => p.ageGroup))], [plans]);
  const filtered = ageGroup === "all" ? plans : plans.filter((p) => p.ageGroup === ageGroup || p.ageGroup === "all");

  return (
    <>
      <SiteHeader />
      <main className="site-shell" style={{ paddingTop: 120, minHeight: "70vh", paddingBottom: 100 }}>
        <p className="eyebrow"><span>06</span>Memberships</p>
        <h1 className="page-title">{hero.title || "Choose your path."}</h1>
        <p className="page-intro">{hero.body || "Flexible plans built around how often you want to train — join as a member for the best value, or drop in whenever it suits you."}</p>

        {error && <p role="alert" style={{ marginTop: 24, color: "#ff6b6b" }}>{error}</p>}

        {!error && ageGroups.length > 1 && (
          <div className="program-filters">
            <button className={`program-filter-chip ${ageGroup === "all" ? "is-active" : ""}`} onClick={() => setAgeGroup("all")}>All ages</button>
            {ageGroups.map((group) => (
              <button key={group} className={`program-filter-chip ${ageGroup === group ? "is-active" : ""}`} onClick={() => setAgeGroup(group)}>
                {ageGroupLabels[group] || group}
              </button>
            ))}
          </div>
        )}

        {!error && !plans.length && <p style={{ marginTop: 40, color: "var(--muted)" }}>Membership plans are being finalized. Check back soon.</p>}

        {!error && plans.length > 0 && (
          <div className="programs-grid">
            {filtered.map((plan) => <PlanTile key={plan._id} plan={plan} />)}
          </div>
        )}
      </main>
      <SiteFooter />
    </>
  );
}
